import { startCase } from 'lodash';
import { FlaggableIngredient } from 'src/gqlReactTypings.generated.d';
import { match, P } from 'ts-pattern';
import Chip, { ChipColor } from 'web-common/src/shared/design-system/components/Chip';
import { Icon, type IconName } from 'web-common/src/shared/design-system/components/Icon';
import { INGREDIENTS_FLAGGED_MAPPINGS } from '../../consts';

interface IngredientsFlaggedChipProps {
  ingredientsFlagged: FlaggableIngredient[];
  customIngredientsFlagged?: string[];
  showIngredientName?: boolean;
}

const getFirstIngredientInfo = (
  ingredientsFlagged: FlaggableIngredient[],
  customIngredientsFlagged: string[]
): { copy: string; iconName: IconName } | null => {
  if (ingredientsFlagged.length > 0) {
    const mappingObject = INGREDIENTS_FLAGGED_MAPPINGS[ingredientsFlagged[0]];
    if (mappingObject) {
      return { copy: mappingObject.copy, iconName: mappingObject.iconName };
    }
  }
  if (customIngredientsFlagged.length > 0) {
    const [firstCustom] = customIngredientsFlagged;
    return { copy: startCase(firstCustom), iconName: 'alert' };
  }
  return null;
};

export const IngredientsFlaggedChip: React.FC<IngredientsFlaggedChipProps> = ({
  ingredientsFlagged,
  customIngredientsFlagged = [],
  showIngredientName = true,
}) => {
  const numIngredientsFlagged = ingredientsFlagged.length + customIngredientsFlagged.length;
  if (numIngredientsFlagged === 0) {
    return null;
  }

  const firstIngredientInfo = getFirstIngredientInfo(ingredientsFlagged, customIngredientsFlagged);
  if (!firstIngredientInfo) {
    return null;
  }

  const { copy: firstIngredientCopy, iconName } = firstIngredientInfo;
  const renderedIcon = <Icon name={iconName} size={16} />;
  const copy = match({ showIngredientName, numIngredientsFlagged, firstIngredientCopy })
    .with({ showIngredientName: true, numIngredientsFlagged: P.when((num) => num === 1), firstIngredientCopy }, () => (
      <span className='pr-1 text-label-400'>{firstIngredientCopy}</span>
    ))
    .with({ showIngredientName: true, numIngredientsFlagged: P.when((num) => num > 1), firstIngredientCopy }, () => (
      <span className='pr-1 text-label-400'>
        {firstIngredientCopy}
        <span className='text-label-600'>&nbsp;+{numIngredientsFlagged - 1}</span>
      </span>
    ))
    .with({ showIngredientName: false, numIngredientsFlagged: P.when((num) => num === 1) }, () => '')
    .with({ showIngredientName: false, numIngredientsFlagged: P.when((num) => num > 1) }, () => (
      <span className='pr-1 text-label-600'>+{numIngredientsFlagged - 1}</span>
    ))
    .otherwise(() => '');

  return <Chip applyFullHeight paddingPixels={4} color={ChipColor.STRAWBERRY_100} copy={copy} icon={renderedIcon} />;
};
